import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { TenantProfileViewStore } from "../../store/TenantProfileViewStore";
import { useRootStore } from "../../../../store/contexts";
import { PersonType } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantProfileViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  personTypeOptions: { id: string; displayId: string }[];
  personType: PersonType | "";
  legalNameValue?: { displayId: string; id: string } | null;
  legalName: string;
  ownPublisherValue: { displayId: string; id: string } | null;
  businessIdValue?: { displayId: string; id: string } | null;
  businessId: string;
  legalEmailValue?: { displayId: string; id: string } | null;
  legalEmail: string;
  streetAndNumberValue?: { displayId: string; id: string } | null;
  streetAndNumber: string;
  floorValue?: { displayId: string; id: string } | null;
  floor: string;
  suiteValue?: { displayId: string; id: string } | null;
  suite: string;
  postalCodeValue?: { displayId: string; id: string } | null;
  postalCode: string;
  countryValue: { displayId: string; id: string } | null;
  provinceValue: { displayId: string; id: string } | null;
  cityValue?: { displayId: string; id: string } | null;
  city: string;
  vatStatusValue: { displayId: string; id: string } | null;
  taxIdValue?: { displayId: string; id: string } | null;
  taxId: string;
  grossReceiptsStatusValue: { displayId: string; id: string } | null;
  grossReceiptsIdValue?: { displayId: string; id: string } | null;
  grossReceiptsId: string;
  billingEmailValue?: { displayId: string; id: string } | null;
  billingEmail: string;
  billingCurrencyValue: { displayId: string; id: string } | null;
  printerNameValue?: { displayId: string; id: string } | null;
  printerName: string;
  printerStreetAndNumberValue?: { displayId: string; id: string } | null;
  printerStreetAndNumber: string;
  printerFloorValue?: { displayId: string; id: string } | null;
  printerFloor: string;
  printerSuiteValue?: { displayId: string; id: string } | null;
  printerSuite: string;
  printerPostalCodeValue?: { displayId: string; id: string } | null;
  printerPostalCode: string;
  printerCountryValue: { displayId: string; id: string } | null;
  printerProvinceValue: { displayId: string; id: string } | null;
  printerCityValue?: { displayId: string; id: string } | null;
  printerCity: string;
  sadaicFtpUrlValue?: { displayId: string; id: string } | null;
  sadaicFtpUrl: string;
  sadaicFtpUserValue?: { displayId: string; id: string } | null;
  sadaicFtpUser: string;
  sadaicEmailValue?: { displayId: string; id: string } | null;
  sadaicEmail: string;
  tenantProfileViewStore: TenantProfileViewStore;
};

export const TenantProfileViewForm: React.FC<TenantProfileViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      personTypeOptions = [],
      personType = "" as PersonType | "",
      legalName = "",
      ownPublisherValue,
      businessId = "",
      legalEmail = "",
      streetAndNumber = "",
      floor = "",
      suite = "",
      postalCode = "",
      countryValue,
      provinceValue,
      city = "",
      vatStatusValue,
      taxId = "",
      grossReceiptsStatusValue,
      grossReceiptsId = "",
      billingEmail = "",
      billingCurrencyValue,
      printerName = "",
      printerStreetAndNumber = "",
      printerFloor = "",
      printerSuite = "",
      printerPostalCode = "",
      printerCountryValue,
      printerProvinceValue,
      printerCity = "",
      sadaicFtpUrl = "",
      sadaicFtpUser = "",
      sadaicEmail = "",
      tenantProfileViewStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="business"
              title={langStore.groupTenantProfileBusinessLabel}
              collapsible={true}
              expanded={tenantProfileViewStore.getCardState("business")}
              toggleExpand={() =>
                tenantProfileViewStore.toggleCardState("business")
              }
            >
              <FormControl fullWidth>
                <ShowString
                  value={
                    personType
                      ? personTypeOptions.find(
                          (o: { id: string }) => o.id === personType
                        )?.displayId || personType
                      : ""
                  }
                  label={langStore.TenantProfilePersonTypeLabel}
                />
                <ShowMultiLine
                  value={legalName}
                  label={langStore.TenantProfileLegalNameLabel}
                />
                <ShowReference
                  label={langStore.TenantProfileOwnPublisherLabel}
                  selectedValue={langStore.displayIdByLang(
                    ownPublisherValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={businessId}
                  label={langStore.TenantProfileBusinessIdLabel}
                />
                <ShowMultiLine
                  value={legalEmail}
                  label={langStore.TenantProfileLegalEmailLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="address"
              title={langStore.groupTenantProfileAddressLabel}
              collapsible={true}
              expanded={tenantProfileViewStore.getCardState("address")}
              toggleExpand={() =>
                tenantProfileViewStore.toggleCardState("address")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={streetAndNumber}
                  label={langStore.TenantProfileStreetAndNumberLabel}
                />
                <ShowMultiLine
                  value={floor}
                  label={langStore.TenantProfileFloorLabel}
                />
                <ShowMultiLine
                  value={suite}
                  label={langStore.TenantProfileSuiteLabel}
                />
                <ShowMultiLine
                  value={postalCode}
                  label={langStore.TenantProfilePostalCodeLabel}
                />
                <ShowReference
                  label={langStore.TenantProfileCountryLabel}
                  selectedValue={langStore.displayIdByLang(
                    countryValue?.displayId
                  )}
                />
                <ShowReference
                  label={langStore.TenantProfileProvinceLabel}
                  selectedValue={langStore.displayIdByLang(
                    provinceValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={city}
                  label={langStore.TenantProfileCityLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="billing"
              title={langStore.groupTenantProfileBillingLabel}
              collapsible={true}
              expanded={tenantProfileViewStore.getCardState("billing")}
              toggleExpand={() =>
                tenantProfileViewStore.toggleCardState("billing")
              }
            >
              <FormControl fullWidth>
                <ShowReference
                  label={langStore.TenantProfileVatStatusLabel}
                  selectedValue={langStore.displayIdByLang(
                    vatStatusValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={taxId}
                  label={langStore.TenantProfileTaxIdLabel}
                />
                <ShowReference
                  label={langStore.TenantProfileGrossReceiptsStatusLabel}
                  selectedValue={langStore.displayIdByLang(
                    grossReceiptsStatusValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={grossReceiptsId}
                  label={langStore.TenantProfileGrossReceiptsIdLabel}
                />
                <ShowMultiLine
                  value={billingEmail}
                  label={langStore.TenantProfileBillingEmailLabel}
                />
                <ShowReference
                  label={langStore.TenantProfileBillingCurrencyLabel}
                  selectedValue={langStore.displayIdByLang(
                    billingCurrencyValue?.displayId
                  )}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="printer"
              title={langStore.groupTenantProfilePrinterLabel}
              collapsible={true}
              expanded={tenantProfileViewStore.getCardState("printer")}
              toggleExpand={() =>
                tenantProfileViewStore.toggleCardState("printer")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={printerName}
                  label={langStore.TenantProfilePrinterNameLabel}
                />
                <ShowMultiLine
                  value={printerStreetAndNumber}
                  label={langStore.TenantProfilePrinterStreetAndNumberLabel}
                />
                <ShowMultiLine
                  value={printerFloor}
                  label={langStore.TenantProfilePrinterFloorLabel}
                />
                <ShowMultiLine
                  value={printerSuite}
                  label={langStore.TenantProfilePrinterSuiteLabel}
                />
                <ShowMultiLine
                  value={printerPostalCode}
                  label={langStore.TenantProfilePrinterPostalCodeLabel}
                />
                <ShowReference
                  label={langStore.TenantProfilePrinterCountryLabel}
                  selectedValue={langStore.displayIdByLang(
                    printerCountryValue?.displayId
                  )}
                />
                <ShowReference
                  label={langStore.TenantProfilePrinterProvinceLabel}
                  selectedValue={langStore.displayIdByLang(
                    printerProvinceValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={printerCity}
                  label={langStore.TenantProfilePrinterCityLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="integrationsSadaic"
              title={langStore.groupTenantProfileIntegrationsSadaicLabel}
              description={
                langStore.groupTenantProfileIntegrationsSadaicDescription
              }
              collapsible={true}
              expanded={tenantProfileViewStore.getCardState(
                "integrationsSadaic"
              )}
              toggleExpand={() =>
                tenantProfileViewStore.toggleCardState("integrationsSadaic")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={sadaicFtpUrl}
                  label={langStore.TenantProfileSadaicFtpUrlLabel}
                />
                <ShowMultiLine
                  value={sadaicFtpUser}
                  label={langStore.TenantProfileSadaicFtpUserLabel}
                />

                <ShowMultiLine
                  value={sadaicEmail}
                  label={langStore.TenantProfileSadaicEmailLabel}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
